import React, { useContext, useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import supabase from "../Config/Supabase";
import DataContext from "../Context/DAtaContext";
import { toast } from "react-toastify";
import {
  Search,
  Plus,
  Trash2,
  X,
  Leaf,
  Wallet,
  Package,
  Tag,
  PenLine,
  AlertTriangle,
  Inbox,
} from "lucide-react";
import UsersContext from "../Context/UsersContext";

const PAGE_SIZE = 10;

function HomePackage() {
  const { vegetableBundle, fetchVegetablebundle } = useContext(DataContext);
  const { user, users } = useContext(UsersContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // collect user profile to get info of user role
  const userData = users?.find((dt) => dt.id === user?.id);

  // Dynamic Calculations based on fetched data
  const totalPackages = vegetableBundle?.length || 0;
  const combinedValue = useMemo(() => {
    return vegetableBundle.reduce(
      (sum, item) => sum + (Number(item.price) || 0),
      0,
    );
  }, [vegetableBundle]);

  const filteredData = vegetableBundle.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Open modal for adding
  const handleAddClick = () => {
    setModalMode("add");
    setFormData({ name: "", price: "" });
    setShowModal(true);
  };

  // Open modal for editing
  const handleEditClick = (item) => {
    setModalMode("edit");
    setSelectedItem(item);
    setFormData({ name: item.name, price: item.price.toString() });
    setShowModal(true);
  };

  /*
   * ---------------------------------------
   * PAGINATION
   * ---------------------------------------
   */

  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));

  const currentPage = Math.min(page, totalPages);

  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const displayedData = filteredData.slice(startIndex, startIndex + PAGE_SIZE);

  // Reset to first page whenever user searches
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Move back to valid page if records are deleted
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.name || !formData.price) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      if (modalMode === "add") {
        const { error } = await supabase
          .from("vegetable_bundle")
          .insert([
            {
              name: formData.name,
              price: parseFloat(formData.price),
            },
          ])
          .select();

        if (error) throw error;

        toast.success("Package added successfully!");
        setShowModal(false);
        setFormData({ name: "", price: "" });
        // fetchVegetablebundle();
      } else {
        const { error } = await supabase
          .from("vegetable_bundle")
          .update({
            name: formData.name,
            price: parseFloat(formData.price),
          })
          .eq("id", selectedItem.id);

        if (error) throw error;

        toast.success("Package updated successfully!");
        setSelectedItem(null);
        setShowModal(false);
        setFormData({ name: "", price: "" });
        fetchVegetablebundle();
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete package interactions
  const handleDel = (item) => {
    setShowDeleteModal(true);
    setSelectedItem(item);
  };

  const handleDelete = async () => {
    setLoading(true);

    try {
      const { error } = await supabase
        .from("vegetable_bundle")
        .delete()
        .eq("id", selectedItem?.id);

      if (error) throw error;
      toast.success("Package deleted successfully!");
      setShowDeleteModal(false);
      setSelectedItem(null);
      fetchVegetablebundle();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl">
              <Leaf className="w-9 h-9 text-[#0ea959]" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight text-gray-900 dark:text-gray-200">
                Packages
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-300">
                Manage your vegetable bundles and their prices
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="hidden grid-cols-1 gap-5 mb-10 md:grid md:grid-cols-2">
          <div className="flex items-center gap-5 p-6 bg-white border border-gray-200 shadow-sm dark:border-slate-900 dark:bg-slate-900 rounded-2xl">
            <div className="w-14 h-14 rounded-full bg-[#e5f5ec] dark:bg-gray-800 flex items-center justify-center">
              <Package className="w-7 h-7 text-[#0ea959]" />
            </div>
            <div>
              <p className="mb-1 text-sm font-medium text-gray-500 dark:text-gray-200">
                Total Packages
              </p>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-200">
                {totalPackages}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-5 p-6 bg-white border border-gray-200 shadow-sm dark:border-slate-900 dark:bg-slate-900 rounded-2xl">
            <div className="w-14 h-14 rounded-full bg-[#e5f5ec] dark:bg-slate-800 flex items-center justify-center">
              <Wallet className="w-7 h-7 text-[#0ea959]" />
            </div>
            <div>
              <p className="mb-1 text-sm font-medium text-gray-500 dark:text-gray-200">
                Combined Value
              </p>
              <h2 className="text-xl font-bold dark:text-gray-200 text-gray-900 flex items-baseline gap-1.5">
                {combinedValue.toLocaleString()}
                <span className="text-sm font-medium text-gray-400 ">TSh</span>
              </h2>
            </div>
          </div>
        </div>

        {/* Toolbar: Search and Add Button */}
        <div className="flex justify-between gap-4 mb-6 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-[340px]">
            <Search className="absolute w-5 h-5 text-gray-400 -translate-y-1/2 left-4 top-1/2" />
            <input
              type="text"
              placeholder="Search packages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white dark:bg-slate-700  dark:text-white  rounded-lg text-sm outline-none focus:border-[#0ea959] focus:ring-1 focus:ring-[#0ea959] transition-all shadow-sm"
            />
          </div>
          <button
            onClick={handleAddClick}
            className="md:inline-flex hidden items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-[#0c954e] transition-colors shadow-sm w-full sm:w-auto"
          >
            <Plus className="w-5 h-5" />
            Add New Package
          </button>
          <button
            onClick={handleAddClick}
            className="inline-flex  md:hidden items-center px-4 justify-center gap-1 py-3 text-xs text-white bg-green-600 rounded-lg hover:bg-[#0c954e] transition-colors"
          >
            <Plus className="w-5 h-5 font-bold text-white" />
            <p className="hidden sm:inline-flex"> Add New Package</p>
          </button>
        </div>

        {/* Table Container for desktop view */}
        <div className="hidden overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm md:block dark:bg-slate-900 dark:border-slate-900">
          <div className="">
            <table className="w-full text-left border-collapse min-w-[700px]">
              <thead className="bg-green-600 ">
                <tr>
                  <th className="hidden w-24 px-8 py-5 text-xs font-bold tracking-wider text-white uppercase border-b border-gray-100 md:table-cell dark:border-gray-700">
                    NO
                  </th>
                  <th className="hidden px-8 py-5 text-xs font-bold tracking-wider text-white uppercase border-b border-gray-100 md:table-cell dark:border-gray-700">
                    PACKAGE NAME
                  </th>
                  <th className="hidden w-48 px-8 py-5 text-xs font-bold tracking-wider text-white uppercase border-b border-gray-100 md:table-cell dark:border-gray-700">
                    PRICE (TSH)
                  </th>
                  {userData?.role === "supervisor" ? (
                    <>
                      <th className="hidden w-32 px-8 py-5 text-xs font-bold tracking-wider text-center text-white uppercase border-b border-gray-100 md:table-cell dark:border-gray-700">
                        ACTIONS
                      </th>
                    </>
                  ) : (
                    <></>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {displayedData.length === 0 ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-8 py-16 text-center text-gray-500"
                    >
                      <motion.div
                        key="empty-mobile"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex flex-col items-center justify-center gap-3 py-16 text-gray-400 bg-white border border-gray-200 shadow-sm dark:bg-slate-900 dark:border-slate-900 rounded-2xl"
                      >
                        <Inbox className="w-9 h-9" />
                        <p className="w-56 px-6 text-sm text-center text-gray-500">
                          No packages found. Add a new one to get started.
                        </p>
                      </motion.div>
                    </td>
                  </tr>
                ) : (
                  displayedData?.map((item, index) => (
                    <>
                      <motion.tr
                        key={item.id + item.name}
                        layout
                        initial={{ opacity: 1, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: 0.25,
                          delay: Math.min(0.03, 0.3),
                        }}
                        className="transition-colors dark:hover:bg-gray-700 hover:bg-gray-50/50 group"
                      >
                        <td className="hidden px-8 py-4 text-sm font-medium text-gray-400 md:table-cell">
                          {(startIndex + index + 1).toString().padStart(2, "0")}
                        </td>
                        <td className="px-8 py-4">
                          <div className="flex flex-col gap-4 md:items-center md:flex-row">
                            <div className="w-9 h-9 rounded-xl bg-[#e5f5ec] dark:bg-slate-800 flex items-center justify-center">
                              <Leaf className="w-4 h-4 text-[#0ea959]" />
                            </div>
                            <span className="text-sm text-black dark:text-white">
                              {item.name}
                            </span>
                          </div>
                        </td>
                        <td className="hidden px-8 py-4 text-sm md:table-cell">
                          <span className="text-black dark:text-white">
                            {item.price.toLocaleString("en-US")}
                          </span>
                          <span className="text-gray-400 ml-1.5 font-medium">
                            TSh
                          </span>
                        </td>
                        {userData?.role === "supervisor" ? (
                          <td className="px-8 py-4">
                            <div className="flex items-center justify-center gap-5">
                              <button
                                onClick={() => handleEditClick(item)}
                                className="text-blue-500 transition-colors hover:text-blue-700"
                                title="Edit"
                              >
                                <PenLine className="w-5 h-5 stroke-[2.5]" />
                              </button>
                              <button
                                onClick={() => handleDel(item)}
                                className="text-red-500 transition-colors hover:text-red-700"
                                title="Delete"
                              >
                                <Trash2 className="w-5 h-5 stroke-[2.5]" />
                              </button>
                            </div>
                          </td>
                        ) : (
                          <></>
                        )}
                      </motion.tr>
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Footer / Pagination */}
          {filteredData.length > 0 && (
            <div className="flex items-center justify-between px-4 py-4 mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-500">
                Showing {startIndex + 1}–
                {Math.min(startIndex + PAGE_SIZE, filteredData.length)} of{" "}
                {filteredData.length} packages
              </p>

              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setPage((current) => Math.max(current - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-xs text-white bg-green-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    Previous
                  </button>

                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {currentPage} / {totalPages}
                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      setPage((current) => Math.min(current + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-xs text-white bg-green-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile card list */}
        <div className="md:hidden">
          <AnimatePresence mode="popLayout" initial={false}>
            {filteredData.length === 0 ? (
              <motion.div
                key="empty-mobile"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center gap-3 py-16 text-gray-400 bg-white border border-gray-200 shadow-sm dark:bg-slate-900 dark:border-slate-900 rounded-2xl"
              >
                <Inbox className="w-9 h-9" />
                <p className="px-6 text-sm text-center text-gray-500">
                  No packages found. Add a new one to get started.
                </p>
              </motion.div>
            ) : (
              <div className="flex flex-col gap-1">
                {displayedData?.map((item, index) => (
                  <motion.div
                    key={item.id + item.name}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{
                      duration: 0.25,
                      delay: Math.min(0.04, 0.3),
                    }}
                    className="flex items-center gap-3 p-4 bg-white border border-gray-200 shadow-sm dark:bg-slate-900 dark:border-slate-900 rounded-2xl"
                  >
                    <div className="w-10 h-10 shrink-0 rounded-xl bg-[#e5f5ec] dark:bg-slate-800 flex items-center justify-center">
                      <Leaf className="w-[18px] h-[18px] text-[#0ea959]" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-black truncate dark:text-white">
                        {item.name}
                      </p>
                      <p className="text-sm">
                        <span className="text-black dark:text-white">
                          {item.price.toLocaleString("en-US")}
                        </span>
                        <span className="ml-1 text-gray-400">TSh</span>
                      </p>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleEditClick(item)}
                        className="p-2 text-blue-500 transition-colors rounded-full hover:text-blue-700 active:bg-blue-50 dark:active:bg-blue-950"
                        title="Edit"
                        aria-label="Edit package"
                      >
                        <PenLine className="w-[18px] h-[18px] stroke-[2.5]" />
                      </motion.button>
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleDel(item)}
                        className="p-2 text-red-500 transition-colors rounded-full hover:text-red-700 active:bg-red-50 dark:active:bg-red-950"
                        title="Delete"
                        aria-label="Delete package"
                      >
                        <Trash2 className="w-[18px] h-[18px] stroke-[2.5]" />
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </AnimatePresence>

          {filteredData.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(current - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-xs font-medium text-white bg-green-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex flex-col items-center">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>

                <span className="text-[11px] text-gray-400">
                  {startIndex + 1}–
                  {Math.min(startIndex + PAGE_SIZE, filteredData.length)} of{" "}
                  {filteredData.length}
                </span>
              </div>

              <button
                type="button"
                onClick={() =>
                  setPage((current) => Math.min(current + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-xs font-medium text-white bg-green-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Bottom-Sheet Modal (Matches image_905cf9.png and image_905dce.png) */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-[2px] sm:p-4">
            {/* Background click listener */}
            <div
              className="absolute inset-0"
              onClick={() => setShowModal(false)}
            />

            <motion.div
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full overflow-hidden bg-white rounded-t-lg shadow-2xl md:max-w-md dark:bg-gray-900 sm:rounded-lg"
            >
              {/* Modal Header */}
              <div className="relative p-4 bg-green-600">
                <button
                  onClick={() => setShowModal(false)}
                  className="absolute text-white transition-colors top-6 right-6 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-11 h-11 bg-white/20 rounded-xl">
                    <Package className="text-white w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold tracking-wide text-white">
                      {modalMode === "add" ? "Add New Package" : "Edit Package"}
                    </h2>
                    <p className="text-sm font-medium text-white/90">
                      Create a new vegetable bundle
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="space-y-6 p-7">
                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-white">
                    <Package className="w-[18px]  h-[18px] text-[#0ea959]" />
                    Package Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-2 dark:bg-gray-700 dark:text-white bg-white sm:border dark:focus:border-green-600 dark:border-gray-600 border-gray-300 rounded-xl text-[15px] font-medium text-gray-800 transition-all outline-none focus:border-[#0ea959] focus:ring-2 focus:ring-[#0ea959]/20 placeholder:text-gray-400 placeholder:font-normal"
                    placeholder="e.g. Poultry SASSO (10 chickens)"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 dark:text-white">
                    <Tag className="w-[18px] h-[18px] text-[#0ea959]" />
                    Price (TSh)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-[15px]">
                      TSh
                    </span>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      className="w-full pl-[56px]  pr-4 py-2 dark:bg-gray-700 dark:text-white dark:focus:border-green-600 dark:border-gray-600 bg-white sm:border border-gray-300 rounded-xl text-[15px] font-medium text-gray-800 transition-all outline-none focus:border-[#0ea959] focus:ring-2 focus:ring-[#0ea959]/20 placeholder:text-gray-400 placeholder:font-normal"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3 px-7 pb-7">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 text-[15px] font-semibold text-gray-700 bg-[#f3f4f6] rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 py-2 text-[15px] font-semibold text-white bg-green-600 rounded-lg hover:bg-[#0c954e] transition-colors disabled:opacity-70 disabled:cursor-not-allowed shadow-md shadow-green-500/20"
                >
                  {loading
                    ? "Saving..."
                    : modalMode === "add"
                      ? "Add Package"
                      : "Update Package"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[2px] p-4">
            <div
              className="absolute inset-0"
              onClick={() => setShowDeleteModal(false)}
            />

            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-sm p-7 bg-white dark:bg-gray-900 rounded-[24px] shadow-2xl text-center"
            >
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-5 rounded-full dark:bg-gray-800 bg-red-50">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>

              <h3 className="mb-2 text-xl font-bold text-gray-900 dark:text-gray-200">
                Delete Package
              </h3>
              <p className="mb-8 text-[15px] dark:text-gray-300 text-gray-500">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-gray-800 dark:text-gray-100">
                  "{selectedItem?.name}"
                </span>
                ? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 py-2 text-[15px] font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex-1 py-2 text-[15px] font-semibold text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors disabled:opacity-70"
                >
                  {loading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default HomePackage;
